/**
 * Attach a ticket (README §5). Three ways to link: create a new ticket
 * one-click, type a number (with or without '#', Enter or Link), or pick a
 * recent one. Idea #1: this modal states plainly that linking IS the human
 * confirmation — there is deliberately no separate "yes it's them" click.
 *
 * `link-ticket`/`create-ticket` are the two operations this modal ever
 * calls (packages/shared/src/operations.ts). Both come back with the
 * updated `thread` (services/ops/src/routes/operations.ts), which is pushed
 * into the store via `upsertThread` so the rest of the console (band,
 * ticket chip, the handoff modal's ticket card) sees the link immediately.
 *
 * Round trip with the handoff modal (resolution #3): if this modal was
 * opened from inside Hand off ("Attach a ticket first" / "Change"), those
 * callers set `pendingHandoff: true` before opening this one. On success
 * this modal reopens the handoff modal instead of just closing, and clears
 * the flag — so attaching from within handoff comes back to handoff with
 * the new ticket visible.
 */
import { useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'
import type { ThreadSummary } from '@communicator/shared'
import { Button, Modal } from '../ui'
import { ops, OpsError } from '../../lib/ops'
import { useStore } from '../../store'
import { modalReads, RECENT_TICKETS_SEAM, type RecentTicketsResult } from './reads'
import {
  ATTACH_IS_CONFIRMATION_TEXT,
  GENERIC_OP_ERROR,
  NO_RECENT_TICKETS_TEXT,
  unwiredText,
  normalizeTicketInput,
} from './format'

export function TicketModal() {
  const modal = useStore((s) => s.modal)
  const activeId = useStore((s) => s.activeId)
  const threads = useStore((s) => s.threads)
  const ticketNum = useStore((s) => s.ticketNum)
  const pendingHandoff = useStore((s) => s.pendingHandoff)
  const set = useStore((s) => s.set)
  const closeModal = useStore((s) => s.closeModal)
  const openModal = useStore((s) => s.openModal)
  const upsertThread = useStore((s) => s.upsertThread)

  const open = modal === 'ticket'
  const thread = threads.find((t) => t.id === activeId) ?? null

  const [result, setResult] = useState<RecentTicketsResult>(RECENT_TICKETS_SEAM)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !activeId) return
    let cancelled = false
    modalReads.recentTickets(activeId).then((r) => {
      if (!cancelled) setResult(r)
    })
    return () => {
      cancelled = true
    }
  }, [open, activeId])

  useEffect(() => {
    if (!open) {
      setError(null)
      setBusy(false)
      setResult(RECENT_TICKETS_SEAM)
    }
  }, [open])

  if (!open) return null

  function finish(updatedThread: ThreadSummary | undefined) {
    if (updatedThread) upsertThread(updatedThread)
    set({ ticketNum: '' })
    if (pendingHandoff) {
      set({ pendingHandoff: false })
      openModal('handoff')
    } else {
      closeModal()
    }
  }

  function reportError(e: unknown) {
    setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
  }

  async function handleCreate() {
    if (!thread || !activeId) return
    // Decided with Andrew 2026-09-01: the dispatcher writes the summary.
    // The old composed subject put one of two canned lines on every ticket,
    // which the team read as noise in Halo. Quick Fix (CallSummary.tsx)
    // deliberately keeps its one-click subject — speed is its whole point.
    const subject = summary.trim()
    if (!subject) return
    setBusy(true)
    setError(null)
    try {
      const result = (await ops.call('create-ticket', {
        threadId: activeId,
        subject,
      })) as { ticket: string; thread: ThreadSummary }
      finish(result.thread)
    } catch (e) {
      reportError(e)
    } finally {
      setBusy(false)
    }
  }

  async function handleLink(rawTicket: string) {
    if (!activeId) return
    const normalized = normalizeTicketInput(rawTicket)
    if (!normalized) {
      setError('Enter a ticket number — digits only, with or without a leading #.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = (await ops.call('link-ticket', { threadId: activeId, ticket: normalized })) as {
        thread: ThreadSummary
      }
      finish(result.thread)
    } catch (e) {
      reportError(e)
    } finally {
      setBusy(false)
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      void handleLink(ticketNum)
    }
  }

  return (
    <Modal open={open} title="Attach a ticket" onClose={closeModal}>
      <p data-testid="ticket-modal-confirmation-copy" className="mb-3 text-[13px] text-mid">
        {ATTACH_IS_CONFIRMATION_TEXT}
      </p>

      {error && (
        <p
          data-testid="ticket-modal-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-bad-subtle px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </p>
      )}

      <div className="mb-4 flex items-center gap-2">
        <input
          data-testid="ticket-modal-summary-input"
          className="h-[34px] flex-1 rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi"
          placeholder="What is this ticket about?"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void handleCreate()
            }
          }}
        />
        <Button
          data-testid="ticket-modal-create-btn"
          // Muted while there is nothing to submit, brand-filled the moment
          // there is — the button's look answers "can I click this yet?"
          // at a glance (Andrew, 2026-09-02).
          variant={summary.trim().length > 0 ? 'primary' : 'secondary'}
          disabled={busy || !thread || summary.trim().length === 0}
          onClick={handleCreate}
        >
          Create a new ticket
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <input
          data-testid="ticket-modal-number-input"
          className="h-[34px] flex-1 rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi"
          placeholder="Ticket number, e.g. 4821 or #4821"
          value={ticketNum}
          onChange={(e) => set({ ticketNum: e.target.value })}
          onKeyDown={handleKeyDown}
        />
        <Button data-testid="ticket-modal-link-btn" disabled={busy} onClick={() => handleLink(ticketNum)}>
          Link
        </Button>
      </div>

      <div data-testid="ticket-modal-recent-section">
        <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Recent tickets</p>
        {!result.wired ? (
          <p data-testid="ticket-modal-recent-unreachable" className="text-[13px] text-warn">
            {unwiredText('tickets', result.reason)}
          </p>
        ) : result.tickets.length === 0 ? (
          <p data-testid="ticket-modal-recent-empty" className="text-[13px] text-lo">
            {NO_RECENT_TICKETS_TEXT}
          </p>
        ) : (
          <div data-testid="ticket-modal-recent-list">
            {result.tickets.map((r) => (
              <button
                key={r.ticket}
                type="button"
                data-testid={`ticket-modal-recent-${r.ticket}`}
                disabled={busy}
                className="mb-1 block w-full rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-left text-[13px] text-hi hover:bg-elevated disabled:opacity-50"
                onClick={() => handleLink(r.ticket)}
              >
                {r.ticket}
                {r.subject ? ` — ${r.subject}` : ''}
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}
